import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Tabs,
  Tab,
  Alert,
  LinearProgress,
  TablePagination
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  FilterList,
  Refresh,
  AttachMoney
} from '@mui/icons-material';
import { Customer, Loan, BorrowerAPIItem } from '../../models/LendingModels';
import { mockLoans } from '../../data/MockData';
import { formatCurrency } from '../../utils/CurrencyFormat';
import { getBorrowers, createBorrowers } from '../../services/request/Borrowers';
import { getDebitsByBorrowers, createDebits } from '../../services/request/Debits';
import { getAllPayments } from '../../services/request/Payments';
import DebitCaptureModal from '../admin/outstandings/DebitCaptureChild';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-tabpanel-${index}`}
      aria-labelledby={`customer-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const CustomerManagement: React.FC = () => {
  const [apiBorrowers, setApiBorrowers] = useState<BorrowerAPIItem[]>([]);
  const [loans] = useState<Loan[]>(mockLoans);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [mainTabValue, setMainTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openDialog, setOpenDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openAddDebitDialog, setOpenAddDebitDialog] = useState(false);
  const [openCaptureDebitDialog, setOpenCaptureDebitDialog] = useState(false);
  const [selectedBorrowerId, setSelectedBorrowerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [newBorrower, setNewBorrower] = useState({ name: '', comments: '' });
  const [debits, setDebits] = useState<any[]>([]);
  const [loadingDebits, setLoadingDebits] = useState(false);
  const [allPayments, setAllPayments] = useState<any[]>([]);
  const [loadingAllPayments, setLoadingAllPayments] = useState(false);
  const [paymentPage, setPaymentPage] = useState(0);
  const [paymentRowsPerPage, setPaymentRowsPerPage] = useState(10);
  const [totalPayments, setTotalPayments] = useState(0);
  const [newDebit, setNewDebit] = useState({
    amount: '',
    outstanding: '',
    interestRate: '',
    durationType: '',
    startDate: '',
    endDate: '',
    paymentDate: '',
    emiAmount: '',
    emiDate: '',
    status: 'ACTIVE'
  });
  const [customerPage, setCustomerPage] = useState(0);
  const [customerRowsPerPage, setCustomerRowsPerPage] = useState(10);
  const [totalBorrowers, setTotalBorrowers] = useState(0);

  // Fetch borrowers from API: http://localhost:1996/api/v1/borrowers?includeOutstanding=true
  const fetchBorrowers = useCallback(async (page = customerPage, pageSize = customerRowsPerPage) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getBorrowers(page, pageSize, {});
      console.log("Response ", response);
      if (response.code === 200) {
        setApiBorrowers(response.result);
        const totalCount = response.totalCount ?? response.result?.length ?? 0;
        setTotalBorrowers(totalCount);
        setApiMessage(`Successfully loaded ${totalCount} borrowers`);
      } else {
        setTotalBorrowers(0);
        setError(`API Error: ${response.message}`);
      }
    } catch (err) {
      setTotalBorrowers(0);
      setError(`Failed to fetch borrowers: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Error fetching borrowers:', err);
    } finally {
      setLoading(false);
    }
  }, [customerPage, customerRowsPerPage]);

  useEffect(() => {
    fetchBorrowers();
  }, [fetchBorrowers]);

  useEffect(() => {
    setCustomerPage(0);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    if (mainTabValue === 1) {
      fetchAllPayments();
    }
  }, [mainTabValue, paymentPage, paymentRowsPerPage]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleViewCustomer = async (borrower: BorrowerAPIItem) => {
    // Open customer debits view in a new tab
    const debitUrl = `/admin/customers/${borrower.id}/debits`;
    window.open(debitUrl, '_blank');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedCustomer(null);
  };

  const handleOpenAddDialog = () => {
    setNewBorrower({ name: '', comments: '' });
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setNewBorrower({ name: '', comments: '' });
  };

  const handleAddBorrower = async () => {
    // Validate form
    if (!newBorrower.name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await createBorrowers({
        name: newBorrower.name,
        comments: newBorrower.comments
      });
      
      console.log('Create borrower response:', response);
      
      if (response && (response.code === 200 || response.code === 201)) {
        setApiMessage('Borrower added successfully!');
        handleCloseAddDialog();
        // Refresh the list
        await fetchBorrowers();
      } else {
        setError(`Failed to add borrower: ${response?.message || 'Unknown error'}`);
      }
    } catch (err) {
      setError(`Failed to add borrower: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Error adding borrower:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch debits for a customer
  const fetchDebits = async (borrowerId: string) => {
    try {
      setLoadingDebits(true);
      const response = await getDebitsByBorrowers(borrowerId, 0, 10, {});
      console.log('Debits response:', response);
      
      if (response.data && response.data.code === 200) {
        setDebits(response.data.result || []);
      } else {
        setDebits([]);
      }
    } catch (err) {
      console.error('Error fetching debits:', err);
      setDebits([]);
    } finally {
      setLoadingDebits(false);
    }
  };

  const handleOpenAddDebitDialog = () => {
    setNewDebit({
      amount: '',
      outstanding: '',
      interestRate: '',
      durationType: 'MONTHLY',
      startDate: '',
      endDate: '',
      paymentDate: '',
      emiAmount: '',
      emiDate: '',
      status: 'ACTIVE'
    });
    setOpenAddDebitDialog(true);
  };

  const handleCloseAddDebitDialog = () => {
    setOpenAddDebitDialog(false);
    setNewDebit({
      amount: '',
      outstanding: '',
      interestRate: '',
      durationType: 'MONTHLY',
      startDate: '',
      endDate: '',
      paymentDate: '',
      emiAmount: '',
      emiDate: '',
      status: 'ACTIVE'
    });
  };

  const handleAddDebit = async () => {
    if (!selectedCustomer) return;

    // Validate required fields
    if (!newDebit.amount || !newDebit.outstanding || !newDebit.startDate) {
      setError('Amount, Outstanding, and Start Date are required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const debitData = {
        amount: parseFloat(newDebit.amount),
        outstanding: parseFloat(newDebit.outstanding),
        interestRate: parseFloat(newDebit.interestRate) || 0,
        durationType: newDebit.durationType,
        startDate: newDebit.startDate,
        endDate: newDebit.endDate,
        paymentDate: parseInt(newDebit.paymentDate) || 1,
        emiAmount: parseFloat(newDebit.emiAmount) || 0,
        emiDate: newDebit.emiDate,
        status: newDebit.status
      };

      const response = await createDebits(selectedCustomer.id, debitData);
      console.log('Create debit response:', response);
      
      if (response && response.data && (response.data.code === 200 || response.data.code === 201)) {
        setApiMessage('Debit added successfully!');
        handleCloseAddDebitDialog();
        // Refresh debits list
        await fetchDebits(selectedCustomer.id);
      } else if (response && (response.code === 200 || response.code === 201)) {
        setApiMessage('Debit added successfully!');
        handleCloseAddDebitDialog();
        // Refresh debits list
        await fetchDebits(selectedCustomer.id);
      } else {
        setError(`Failed to add debit: ${response?.data?.message || response?.message || 'Unknown error'}`);
      }
    } catch (err) {
      setError(`Failed to add debit: ${err instanceof Error ? err.message : 'Unknown error'}`);
      console.error('Error adding debit:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllPayments = async () => {
    try {
      setLoadingAllPayments(true);
      const response = await getAllPayments(paymentPage, paymentRowsPerPage);
      
      if (response.data && response.data.code === 200) {
        setAllPayments(response.data.result || []);
        setTotalPayments(response.data.totalCount || 0);
      } else if (response.status === 200 && response.data) {
        setAllPayments(response.data.result || []);
        setTotalPayments(response.data.totalCount || 0);
      }
    } catch (err) {
      console.error('Error fetching all payments:', err);
      setError('Failed to fetch payment history');
    } finally {
      setLoadingAllPayments(false);
    }
  };

  const handleMainTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setMainTabValue(newValue);
  };

  const handleCustomerPageChange = (_event: unknown, newPage: number) => {
    setCustomerPage(newPage);
  };

  const handleCustomerRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomerRowsPerPage(Number.parseInt(event.target.value, 10));
    setCustomerPage(0);
  };

  const handlePaymentPageChange = (event: unknown, newPage: number) => {
    setPaymentPage(newPage);
  };

  const handlePaymentRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaymentRowsPerPage(Number.parseInt(event.target.value, 10));
    setPaymentPage(0);
  };

  const handleOpenCaptureDebitDialog = (borrowerId: string) => {
    setSelectedBorrowerId(borrowerId);
    setOpenCaptureDebitDialog(true);
  };

  const handleCloseCaptureDebitDialog = () => {
    setOpenCaptureDebitDialog(false);
    setSelectedBorrowerId(null);
  };

  const handleDebitCreated = () => {
    // Refresh the borrowers list after debit is created
    fetchBorrowers();
    setApiMessage('Debit captured successfully!');
  };

  const filteredCustomers = apiBorrowers.filter(borrower => {
    const matchesSearch = borrower.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         borrower.comments.toLowerCase().includes(searchTerm.toLowerCase());
    // Map creditStatus to comparable status
    const statusMapping: { [key: string]: string } = {
      'GOOD': 'active',
      'BAD': 'inactive',
      'BLOCKED': 'blacklisted'
    };
    const borrowerStatus = statusMapping[borrower.creditStatus] || 'inactive';
    const matchesStatus = statusFilter === 'all' || borrowerStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getCustomerOutstanding = (borrowerId: string) => {
    const borrower = apiBorrowers.find(b => b.id === borrowerId);
    return borrower ? borrower.totalOutstanding : 0;
  };

  const getCustomerLoans = (customerId: string) => {
    return loans.filter(loan => loan.customerId === customerId);
  };

  const getStatusColor = (creditStatus: string) => {
    switch (creditStatus) {
      case 'GOOD': return 'success';
      case 'BAD': return 'error';
      case 'BLOCKED': return 'error';
      default: return 'default';
    }
  };

  const getCreditStatusLabel = (creditStatus: string) => {
    switch (creditStatus) {
      case 'GOOD': return 'Active';
      case 'BAD': return 'Inactive';
      case 'BLOCKED': return 'Blacklisted';
      default: return 'Unknown';
    }
  };

  const getChipColor = (creditStatus: string): 'success' | 'warning' | 'error' => {
    if (creditStatus === 'GOOD') return 'success';
    if (creditStatus === 'BAD') return 'warning';
    return 'error';
  };

  const getEmploymentStatusColor = (status: string) => {
    switch (status) {
      case 'employed': return 'success';
      case 'self-employed': return 'info';
      case 'unemployed': return 'warning';
      case 'retired': return 'default';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Customer Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => fetchBorrowers()}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenAddDialog}
          >
            Add Customer
          </Button>
        </Box>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {apiMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setApiMessage(null)}>
          {apiMessage}
        </Alert>
      )}

      {/* Main Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={mainTabValue} onChange={handleMainTabChange}>
          <Tab label="Customers" />
          <Tab label="Payment History" />
        </Tabs>
      </Box>

      {/* Tab Panel: Customers */}
      {mainTabValue === 0 && (
        <>
          {/* Search and Filter */}
          <Paper sx={{ p: 3, mb: 4 }}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  fullWidth
                  label="Search customers"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <FormControl fullWidth>
                  <InputLabel>Status Filter</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status Filter"
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                    <MenuItem value="blacklisted">Blacklisted</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12} md={4} lg={3}>
                <Button
                  variant="outlined"
                  startIcon={<FilterList />}
                  fullWidth
                >
                  More Filters
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* Customer List */}
          <Paper>
            {!loading && filteredCustomers.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>No borrowers found</Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your search criteria or use the refresh button to reload data
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Customer</TableCell>
                      <TableCell align="right">Outstanding</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Employment</TableCell>
                      <TableCell align="center">Loans</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredCustomers.map((borrower) => (
                      <TableRow key={borrower.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ mr: 2 }}>
                              {borrower.name.charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2">
                                {borrower.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ID: {borrower.id.substring(0, 8)}...
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h6" color="error.main" fontWeight="bold">
                            {formatCurrency(getCustomerOutstanding(borrower.id))}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Outstanding
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">N/A</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Phone not available
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getCreditStatusLabel(borrower.creditStatus)}
                            color={getStatusColor(borrower.creditStatus) as any}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Chip
                              label="Credit Decision"
                              color={getChipColor(borrower.creditStatus)}
                              size="small"
                              sx={{ mb: 0.5 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {borrower.comments}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            API Data
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleViewCustomer(borrower)}
                            title="View Customer Debits"
                          >
                            <Visibility />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="success"
                            onClick={() => handleOpenCaptureDebitDialog(borrower.id)}
                            title="Capture Debit"
                          >
                            <AttachMoney />
                          </IconButton>
                          <IconButton size="small" title="Edit Customer">
                            <Edit />
                          </IconButton>
                          <IconButton size="small" color="error" title="Delete Customer">
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {totalBorrowers > 0 && (
              <TablePagination
                component="div"
                count={totalBorrowers}
                page={customerPage}
                onPageChange={handleCustomerPageChange}
                rowsPerPage={customerRowsPerPage}
                onRowsPerPageChange={handleCustomerRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
              />
            )}
          </Paper>
        </>
      )}

      {/* Tab Panel: Payment History */}
      {mainTabValue === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Global Payment History</Typography>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchAllPayments}
              disabled={loadingAllPayments}
            >
              Refresh
            </Button>
          </Box>

          {loadingAllPayments && <LinearProgress sx={{ mb: 2 }} />}

          {allPayments.length === 0 && !loadingAllPayments ? (
            <Alert severity="info">
              No payment records found.
            </Alert>
          ) : (
            <Paper>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Payment ID</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Debit ID</TableCell>
                      <TableCell>Borrower</TableCell>
                      <TableCell>Received By</TableCell>
                      <TableCell>Note</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allPayments.map((payment) => {
                      const borrower = apiBorrowers.find(b => 
                        debits.some(d => d.id === payment.debitId && d.borrowerId === b.id)
                      );
                      
                      return (
                        <TableRow key={payment.id || payment.paymentId} hover>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {(payment.id || payment.paymentId || '').substring(0, 8)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {payment.createdDate ? new Date(payment.createdDate).toLocaleDateString() : 
                             payment.createdAt ? new Date(payment.createdAt).toLocaleDateString() : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="success.main" fontWeight="bold">
                              {formatCurrency(payment.amount || 0)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={payment.paymentType || 'N/A'} 
                              size="small"
                              color={
                                payment.paymentType === 'REPAYMENT' ? 'success' : 
                                payment.paymentType === 'TOP_UP' ? 'info' : 
                                payment.paymentType === 'INTEREST' ? 'warning' :
                                'default'
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {(payment.debitId || 'N/A').substring(0, 8)}...
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {borrower?.name || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>{payment.paymentReceivedBy || 'N/A'}</TableCell>
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                              {payment.note || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={totalPayments}
                page={paymentPage}
                onPageChange={handlePaymentPageChange}
                rowsPerPage={paymentRowsPerPage}
                onRowsPerPageChange={handlePaymentRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
              />
            </Paper>
          )}
        </Box>
      )}

      {/* Customer Detail Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Customer Details
          {selectedCustomer && (
            <Typography variant="body2" color="text.secondary">
              {selectedCustomer.firstName} {selectedCustomer.lastName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ minWidth: { xs: '100%', sm: 600, md: 800 } }}>
          {selectedCustomer && (
            <Box>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label="Profile" />
                {/* <Tab label="Loans" /> */}
                <Tab label="Debits" />
                <Tab label="Payments" />
                {/* <Tab label="Documents" /> */}
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={3} sx={{ mt: 1 }}>
                  <Grid item xs={12} lg={6}>
                    <Card sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" gutterBottom>Personal Information</Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Full Name</Typography>
                        <Typography variant="body1">
                          {selectedCustomer.firstName} {selectedCustomer.lastName}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Email</Typography>
                        <Typography variant="body1">{selectedCustomer.email}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Phone</Typography>
                        <Typography variant="body1">{selectedCustomer.phone}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Date of Birth</Typography>
                        <Typography variant="body1">
                          {selectedCustomer.dateOfBirth.toLocaleDateString()}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <Card sx={{ p: 2, height: '100%' }}>
                      <Typography variant="h6" gutterBottom>Financial Information</Typography>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Credit Score</Typography>
                        <Typography variant="body1">
                          {selectedCustomer.creditScore || 'Not available'}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Monthly Income</Typography>
                        <Typography variant="body1">
                          {formatCurrency(selectedCustomer.monthlyIncome)}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary">Employment Status</Typography>
                        <Chip
                          label={selectedCustomer.employmentStatus}
                          color={getEmploymentStatusColor(selectedCustomer.employmentStatus) as any}
                          size="small"
                        />
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>
{/* 
              <TabPanel value={tabValue} index={1}>
                <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>Active Loans</Typography>
                <Grid container spacing={2}>
                  {getCustomerLoans(selectedCustomer.id).map((loan) => (
                    <Grid item xs={12} md={6} lg={4} key={loan.id}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="subtitle1" gutterBottom>{loan.loanNumber}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {loan.loanType} - {loan.purpose}
                          </Typography>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Principal</Typography>
                              <Typography variant="body1">
                                {formatCurrency(loan.principalAmount)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="body2" color="text.secondary">Interest Rate</Typography>
                              <Typography variant="body1">
                                {loan.interestRate}%
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </TabPanel> */}

              <TabPanel value={tabValue} index={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                  <Typography variant="h6">Debits</Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpenAddDebitDialog}
                    size="small"
                  >
                    Add Debit
                  </Button>
                </Box>

                {loadingDebits ? (
                  <LinearProgress />
                ) : debits.length === 0 ? (
                  <Alert severity="info">
                    No debits found for this customer. Click "Add Debit" to create one.
                  </Alert>
                ) : (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Amount</TableCell>
                          <TableCell>Outstanding</TableCell>
                          <TableCell>Interest Rate</TableCell>
                          <TableCell>Start Date</TableCell>
                          <TableCell>End Date</TableCell>
                          <TableCell>EMI Amount</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {debits.map((debit, index) => (
                          <TableRow key={index} hover>
                            <TableCell>{formatCurrency(debit.amount || 0)}</TableCell>
                            <TableCell>{formatCurrency(debit.outstanding || 0)}</TableCell>
                            <TableCell>{debit.interestRate || 0}%</TableCell>
                            <TableCell>{debit.startDate || 'N/A'}</TableCell>
                            <TableCell>{debit.endDate || 'N/A'}</TableCell>
                            <TableCell>{formatCurrency(debit.emiAmount || 0)}</TableCell>
                            <TableCell>
                              <Chip 
                                label={debit.status || 'N/A'} 
                                color={debit.status === 'ACTIVE' ? 'success' : 'default'}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Typography variant="h6" gutterBottom>Payment History</Typography>
                <Alert severity="info">
                  Payment history will be displayed here
                </Alert>
              </TabPanel>

              {/* <TabPanel value={tabValue} index={4}>
                <Typography variant="h6" gutterBottom>Documents</Typography>
                <Alert severity="info">
                  Customer documents will be displayed here
                </Alert>
              </TabPanel> */}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          <Button variant="contained">Edit Customer</Button>
        </DialogActions>
      </Dialog>

      {/* Add Borrower Dialog */}
      <Dialog
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Borrower</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={newBorrower.name}
              onChange={(e) => setNewBorrower({ ...newBorrower, name: e.target.value })}
              placeholder="Enter borrower name"
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Comments"
              value={newBorrower.comments}
              onChange={(e) => setNewBorrower({ ...newBorrower, comments: e.target.value })}
              placeholder="Enter comments (e.g., Well Known Friend)"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddBorrower}
            disabled={loading || !newBorrower.name.trim()}
          >
            Add Borrower
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Debit Dialog */}
      <Dialog
        open={openAddDebitDialog}
        onClose={handleCloseAddDebitDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Debit</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={newDebit.amount}
                  onChange={(e) => setNewDebit({ ...newDebit, amount: e.target.value })}
                  placeholder="Enter amount"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Outstanding"
                  type="number"
                  value={newDebit.outstanding}
                  onChange={(e) => setNewDebit({ ...newDebit, outstanding: e.target.value })}
                  placeholder="Enter outstanding amount"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Interest Rate (%)"
                  type="number"
                  value={newDebit.interestRate}
                  onChange={(e) => setNewDebit({ ...newDebit, interestRate: e.target.value })}
                  placeholder="e.g., 0.1"
                  inputProps={{ step: "0.01" }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Duration Type</InputLabel>
                  <Select
                    value={newDebit.durationType}
                    label="Duration Type"
                    onChange={(e) => setNewDebit({ ...newDebit, durationType: e.target.value })}
                  >
                    <MenuItem value="DAILY">Daily</MenuItem>
                    <MenuItem value="WEEKLY">Weekly</MenuItem>
                    <MenuItem value="MONTHLY">Monthly</MenuItem>
                    <MenuItem value="YEARLY">Yearly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={newDebit.startDate}
                  onChange={(e) => setNewDebit({ ...newDebit, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={newDebit.endDate}
                  onChange={(e) => setNewDebit({ ...newDebit, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Payment Date (Day of Month)"
                  type="number"
                  value={newDebit.paymentDate}
                  onChange={(e) => setNewDebit({ ...newDebit, paymentDate: e.target.value })}
                  placeholder="e.g., 2"
                  inputProps={{ min: 1, max: 31 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="EMI Amount"
                  type="number"
                  value={newDebit.emiAmount}
                  onChange={(e) => setNewDebit({ ...newDebit, emiAmount: e.target.value })}
                  placeholder="Enter EMI amount"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="EMI Date"
                  type="date"
                  value={newDebit.emiDate}
                  onChange={(e) => setNewDebit({ ...newDebit, emiDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={newDebit.status}
                    label="Status"
                    onChange={(e) => setNewDebit({ ...newDebit, status: e.target.value })}
                  >
                    <MenuItem value="ACTIVE">Active</MenuItem>
                    <MenuItem value="CLOSED">Closed</MenuItem>
                    <MenuItem value="PENDING">Pending</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddDebitDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleAddDebit}
            disabled={loading || !newDebit.amount || !newDebit.outstanding || !newDebit.startDate}
          >
            Add Debit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Capture Debit Modal */}
      {selectedBorrowerId && (
        <DebitCaptureModal
          open={openCaptureDebitDialog}
          borrowerId={selectedBorrowerId}
          handleDebitModelClose={handleCloseCaptureDebitDialog}
          onDebitCreated={handleDebitCreated}
        />
      )}
    </Box>
  );
};

export default CustomerManagement;
